import { useEffect, useRef, useState, memo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useUser } from "../../../context/UserContext";
import { Bell, Settings, LogOut, User, Sparkles, Menu, ToggleLeft, ToggleRight } from "lucide-react";
import ParkProLogoWhiteLong from "../../../assets/ParkPro White_Long.svg";
import NotificationDropdown from "../NotificationDropdown";
import "../../../styles/modern-design-system.css";
import { canAccessAdmin } from "../../../utils/roleUtils";
import { useNavMode } from "../../../hooks/useNavMode";

const ModernTopbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const [menuOpen, setMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { navMode, setNavMode, hasAdminAccess } = useNavMode();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  const fullName =
    user?.firstname || user?.lastname
      ? `${user?.firstname || ""} ${user?.lastname || ""}`.trim()
      : user?.username || "";

  const initials =
    user?.firstname?.[0] || user?.lastname?.[0]
      ? `${user?.firstname?.[0] || ""}${user?.lastname?.[0] || ""}`
      : user?.username?.[0]?.toUpperCase() || "U";

  const handleLogout = async () => {
    try {
      await fetch(`${import.meta.env.VITE_API_URL}/api/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (e) {
      console.error("Logout error:", e);
    } finally {
      window.location.href = "/agent/login";
    }
  };

  return (
    <header className="sticky top-0 z-[100] w-full bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 border-b backdrop-blur-sm border-gray-700/50">
      <div className="flex justify-between items-center px-3 py-3 h-16 sm:px-6 sm:py-4 sm:h-20">
        {/* Logo Section */}
        <div className="flex items-center space-x-3 sm:space-x-6">
          <div className="flex items-center">
            <img
              src={ParkProLogoWhiteLong}
              alt="ParkPro Logo"
              className="w-auto h-8 transition-transform duration-300 hover:scale-105 sm:h-12"
              style={{ objectFit: "contain" }}
            />
            <div className="hidden items-center ml-12 sm:flex sm:ml-16">
              <Sparkles className="w-3 h-3 text-white animate-pulse sm:w-4 sm:h-4" />
              <span className="ml-1 text-sm font-medium text-white">
                {canAccessAdmin(user?.role) ? "Admin Portal" : "Agent Portal"}
              </span>
            </div>
          </div>
        </div>


        {/* User Section */}
        <div className="flex items-center space-x-2 sm:space-x-4">
          {/* Admin Mode Toggle - Only show if user has admin access */}
          {hasAdminAccess && (
            <div className="hidden sm:flex items-center px-3 py-1.5 bg-gray-800/50 rounded-lg border border-gray-700/50">
              <button
                onClick={() => {
                  const newMode = navMode === 'admin' ? 'agent' : 'admin';
                  setNavMode(newMode);
                  // If switching to Agent Mode while on admin route, navigate away
                  // This prevents auto-switch from immediately switching back
                  if (newMode === 'agent' && location.pathname.startsWith('/agent/admin')) {
                    navigate('/agent/dashboard');
                  }
                }}
                className="flex items-center space-x-2 text-sm text-white hover:text-gray-200 transition-colors"
                aria-label={navMode === 'admin' ? 'Switch to Agent Mode' : 'Switch to Admin Mode'}
              >
                {navMode === 'admin' ? (
                  <>
                    <ToggleRight className="w-4 h-4" />
                    <span className="text-xs font-medium">Admin Mode</span>
                  </>
                ) : (
                  <>
                    <ToggleLeft className="w-4 h-4" />
                    <span className="text-xs font-medium">Switch to Admin</span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* Notifications */}
          <NotificationDropdown />

          {/* User Menu */}
          {user && (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="flex items-center p-1.5 space-x-2 rounded-lg transition-all duration-200 hover:bg-gray-700/50 group sm:p-2 sm:space-x-3"
              >
                {/* Avatar */}
                <div className="relative">
                  <div className="flex justify-center items-center w-8 h-8 bg-gradient-to-br rounded-full border-2 shadow-lg from-primary-500 to-primary-600 border-primary-400/50 sm:w-10 sm:h-10">
                    <span className="text-xs font-bold text-white sm:text-sm">
                      {initials}
                    </span>
                  </div>
                  <div className="absolute -right-0.5 -bottom-0.5 w-2 h-2 bg-green-400 rounded-full border-2 border-gray-900 sm:-right-1 sm:-bottom-1 sm:w-3 sm:h-3"></div>
                </div>

                {/* User Info - Hidden on mobile */}
                <div className="hidden text-left lg:block">
                  <div className="text-xs font-semibold text-white sm:text-sm">
                    {fullName}
                  </div>
                  <div className="text-xs text-gray-400">Travel Agent</div>
                </div>

                {/* Dropdown Arrow */}
                <svg
                  className={`w-3 h-3 text-gray-400 transition-transform duration-200 ${
                    menuOpen ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {menuOpen && (
                <div className="absolute right-0 z-50 py-2 mt-2 w-56 bg-white rounded-xl border border-gray-200 shadow-2xl animate-scale-in sm:w-64">
                  {/* User Info Header */}
                  <div className="px-3 py-3 border-b border-gray-100 sm:px-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex justify-center items-center w-8 h-8 bg-gradient-to-br rounded-full from-primary-500 to-primary-600 sm:w-10 sm:h-10">
                        <span className="text-xs font-bold text-white sm:text-sm">
                          {initials}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-900 truncate">
                          {fullName}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {user?.email || "Not available"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Menu Items */}
                  <div className="py-1">
                    {/* Admin Mode Toggle - Mobile */}
                    {hasAdminAccess && (
                      <button
                        onClick={() => {
                          const newMode = navMode === 'admin' ? 'agent' : 'admin';
                          setNavMode(newMode);
                          // If switching to Agent Mode while on admin route, navigate away
                          if (newMode === 'agent' && location.pathname.startsWith('/agent/admin')) {
                            navigate('/agent/dashboard');
                          }
                          setMenuOpen(false);
                        }}
                        className="flex items-center px-3 py-2 w-full text-sm text-gray-700 transition-colors hover:bg-gray-100 sm:px-4"
                      >
                        {navMode === 'admin' ? (
                          <>
                            <ToggleRight className="mr-3 w-4 h-4" />
                            Exit Admin Mode
                          </>
                        ) : (
                          <>
                            <ToggleLeft className="mr-3 w-4 h-4" />
                            Switch to Admin Mode
                          </>
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        navigate("/agent/settings");
                      }}
                      className="flex items-center px-3 py-2 w-full text-sm text-gray-700 transition-colors hover:bg-gray-100 sm:px-4"
                    >
                      <Settings className="mr-3 w-4 h-4" />
                      Settings
                    </button>
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        handleLogout();
                      }}
                      className="flex items-center px-3 py-2 w-full text-sm text-red-600 transition-colors hover:bg-red-50 sm:px-4"
                    >
                      <LogOut className="mr-3 w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

const Topbar = memo(ModernTopbar);
export default Topbar;
export { ModernTopbar }; // Backward compatibility